import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, RefreshCw } from 'lucide-react';
import {
  type CategoryAdminItem,
  type CreateCategoryInput,
  createCategorySchema,
} from '@news-cms/shared';
import { CoverImagePicker } from '@/components/posts/cover-image-picker';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { slugifyForPreview } from '@/lib/slugify';
import { taxonomyErrorMessage, useCreateCategory, useUpdateCategory } from '@/lib/taxonomies';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set → edit mode. When undefined → create mode. */
  editing?: CategoryAdminItem;
  /** Other categories — used to populate parent select (excludes editing.id). */
  allCategories: CategoryAdminItem[];
}

export function CategoryEditDialog({ open, onOpenChange, editing, allCategories }: Props) {
  const isEdit = Boolean(editing);
  const create = useCreateCategory();
  const update = useUpdateCategory();
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  // Cover image is local state — RHF would be overkill for a single
  // id-pick action. Mirrors the same shape CoverImagePicker expects
  // and emits.
  const [coverImage, setCoverImage] = useState<{
    id: string;
    url: string;
    altText: string | null;
  } | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm<CreateCategoryInput>({
    resolver: zodResolver(createCategorySchema),
    defaultValues: {
      name: '',
      slug: '',
      description: '',
      parentId: null,
    },
  });

  // Reset form when opening or switching editing target
  useEffect(() => {
    if (!open) return;
    setSlugManuallyEdited(Boolean(editing?.slug));
    reset({
      name: editing?.name ?? '',
      slug: editing?.slug ?? '',
      description: editing?.description ?? '',
      parentId: editing?.parentId ?? null,
    });
    setCoverImage(editing?.coverImage ?? null);
  }, [open, editing, reset]);

  const error = create.error ?? update.error;
  const isPending = create.isPending || update.isPending;

  const onSubmit = handleSubmit(async (data) => {
    const payload: CreateCategoryInput = {
      ...data,
      // empty string → null so backend treats it as cleared
      parentId: data.parentId || null,
      description: data.description?.trim() ? data.description : null,
      coverImageId: coverImage?.id ?? null,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, input: payload });
      } else {
        await create.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch {
      // surfaced via error
    }
  });

  const parentOptions = allCategories.filter((c) => c.id !== editing?.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'แก้ไขหมวด' : 'สร้างหมวดใหม่'}</DialogTitle>
          <DialogDescription>
            หมวดถูกใช้เพื่อจัดกลุ่มบทความ — slug ใช้ใน URL (`/api/public/posts?category=slug`)
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{taxonomyErrorMessage(error)}</AlertDescription>
            </Alert>
          )}

          <div>
            <Label htmlFor="cat-name" className="mb-1.5 block">
              ชื่อ *
            </Label>
            <Input
              id="cat-name"
              {...register('name', {
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  if (!slugManuallyEdited && !isEdit) {
                    setValue('slug', slugifyForPreview(e.target.value, 100));
                  }
                },
              })}
              placeholder="หมวดข่าว"
            />
            {errors.name && <p className="text-destructive mt-1 text-xs">{errors.name.message}</p>}
          </div>

          <div>
            <Label htmlFor="cat-slug" className="mb-1.5 block">
              Slug
            </Label>
            <div className="flex gap-2">
              <Input
                id="cat-slug"
                {...register('slug', {
                  onChange: () => setSlugManuallyEdited(true),
                })}
                placeholder="news"
                className="font-mono"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  const name = watch('name') ?? '';
                  setValue('slug', slugifyForPreview(name, 100));
                  setSlugManuallyEdited(false);
                }}
                aria-label="สร้าง slug จากชื่อ"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            {errors.slug && <p className="text-destructive mt-1 text-xs">{errors.slug.message}</p>}
          </div>

          <div>
            <Label htmlFor="cat-parent" className="mb-1.5 block">
              หมวดแม่
            </Label>
            <select
              id="cat-parent"
              // `setValueAs` coerces the DOM's empty-string ("— ไม่มี —"
              // option) to null BEFORE zodResolver runs. Without it,
              // Zod's .uuid() rejects "" and the form silently refuses
              // to submit when an admin tries to demote a child back to
              // top-level. See the parentId schema in
              // packages/shared/src/schemas/taxonomy.ts.
              {...register('parentId', {
                setValueAs: (v) => (v === '' || v == null ? null : String(v)),
              })}
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 text-sm"
            >
              <option value="">— ไม่มี (top-level) —</option>
              {parentOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {errors.parentId && (
              <p className="text-destructive mt-1 text-xs">{errors.parentId.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="cat-desc" className="mb-1.5 block">
              รายละเอียด
            </Label>
            <Textarea
              id="cat-desc"
              {...register('description')}
              placeholder="คำอธิบายหมวดนี้ (ใช้ใน SEO/landing page)"
              rows={2}
            />
          </div>

          <div>
            <Label className="mb-1.5 block">รูปปก</Label>
            <CoverImagePicker selected={coverImage} onChange={setCoverImage} />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              ยกเลิก
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? isEdit
                  ? 'กำลังบันทึก...'
                  : 'กำลังสร้าง...'
                : isEdit
                  ? 'บันทึก'
                  : 'สร้าง'}
            </Button>
          </DialogFooter>

          {/* Preview hint outside the watch — uses RHF watch */}
          <SlugPreview watch={watch} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Tiny child so we don't subscribe the whole parent to slug changes
function SlugPreview({
  watch,
}: {
  watch: ReturnType<typeof useForm<CreateCategoryInput>>['watch'];
}) {
  const slug = watch('slug');
  return (
    <p className="text-[11px] text-slate-500">
      Public URL filter: <code className="font-mono">?category={slug || '(auto)'}</code>
    </p>
  );
}
